import axios from 'axios';
import { isEmpty, isLoggedIn, setLoginStatus } from '../helper/utils';
import router from '../router';

// getProtectedData START
/**
* @typedef {Object} DataResponse
* @property {*} response Response data or error message (string)
* @property {Boolean} isOk true if GET request was successful
* @property {Boolean} isRefreshOk true if request for new access token was successful (if
* previously unauthenticated)
*/
/**
* GET request handler for protected endpoints.
* If retrieved successfully, returns response data.
* If 401, attempt to get new access token with the current refresh * token, then try accessing the
* protected endpoint again; if failed once more, redirect to login page.
* If 403, returns custom error message to display for users.
* Any other error message will be returned as a string as well.
* @function getProtectedData
* @param {String} endpoint e.g. "/protected"
* @param {Object} requestConfig any additional parameters
* @param {Function} setData React hook to set data, if any
* @param {Function} setError React hook to set data, if any
* @param {Boolean} retryRefresh Determines whether to request a new access token if unauthorized (401)
* @returns {Promise<DataResponse|undefined>}
*/
export async function getProtectedData(
  endpoint, requestConfig={}, setData=null, setError=null, retryRefresh=true,
) {
  const parsedEndpoint = endpoint.split("/").filter(val => val.length).join("/") + (
    requestConfig && requestConfig.params && !isEmpty(requestConfig.params) ? "/" : ""
  );
  return axios.get(
    `${process.env.REACT_APP_SERVER_URL}/${parsedEndpoint}`,
    { ...(isEmpty(requestConfig) ? {} : requestConfig), withCredentials: true }
  ).then((response) => {
    useDataSetter(setData, response.data, endpoint);
    return wrapResultInPromise({response: response.data, isOk: true, isRefreshOk: true});
  }).catch(async (error) => {
    let errorMessage = `Error fetching data from ${endpoint}: ${error.message}`;
    console.error(errorMessage);
    if (error.response) {
      if (error.response.status == 401) {
        if (retryRefresh) {
          return axios.post(
            `${process.env.REACT_APP_SERVER_URL}/token/refresh/`, {}, { withCredentials: true }
          ).then(() => (
            wrapResultInPromise(getProtectedData(endpoint, requestConfig, setData, setError, false))
          )).catch(() => {
            useDataSetter(setError, errorMessage, endpoint);
            redirectToLogin();
          });
        }
        errorMessage = "Something went wrong while fetching data; please contact your administrator.";
        useDataSetter(setError, errorMessage, endpoint);
        redirectToLogin();
      } else if (error.response.status == 403) {
        errorMessage = "You are not allowed to access this data; please contact your administrator.";
      }
    }
    useDataSetter(setError, errorMessage, endpoint);
    return wrapResultInPromise({response: errorMessage, isOk: false, isRefreshOk: false});
  });
}

const useDataSetter = (func, data, endpoint) => {
  if (func && func instanceof Function) {
    try {
      func(data);
    } catch (err) {
      console.error(`Error setting error message for ${endpoint}: ${err}`);
    }
  }
}

export const redirectToLogin = () => {
  // setLoginStatus(false);
  // router.navigate('/', {replace: true});
  console.log("User not authenticated; redirected to login page");
}

const wrapResultInPromise = (result) => new Promise((resolve) => resolve(result));
// getProtectedData END